/*
 * Normalizers are used by the API middleware to take responses and convert them be used in our
 * Redux state tree. Part of the work is converting the taxonomy the API uses (project, collection)
 * to the user-facing taxonomy (kit, group). The more important aspect is normalizing the nested
 * objects into a flat representation, so that the entities can be shared without duplicating data.
 */

import { TinyColor } from "@ctrl/tinycolor";
import { AssetType, ItemType } from "../constants/types";
import { slugifyName } from "../helpers/slugify";

import {
  AnyObject,
  Space,
  SpaceTheme,
  SpaceMember,
  Kit,
  KitVersion,
  KitMember,
  KitAccess,
  Item,
  Asset,
  Color,
  SpacePreview,
  Portal,
  PortalItem,
  PortalMember,
  ThemeHeaderTitleOptions,
  Product,
  Section,
} from "../models";

// Helper to cast unknown to Record<string, unknown>
function asObj(val: unknown): Record<string, unknown> {
  return val as Record<string, unknown>;
}
// Helper to cast unknown to array
function asArr(val: unknown, def = []): unknown[] {
  return Array.isArray(val) ? val : def;
}

export function camelCaseKeys(object: AnyObject): AnyObject {
  const snakeToCamel = (str: string) =>
    str.replace(/([-_][a-z0-9])/g, group => group.toUpperCase().replace("-", "").replace("_", ""));
  return Object.keys(object).reduce((acc, key) => {
    acc[key.startsWith("__") ? key : snakeToCamel(key)] = object[key];
    return acc;
  }, {});
}

// MARK : Space
/*-------------------------------------------------------------------------------*/
export function formatSpace(data: AnyObject): Space {
  const avatars = asObj(data.avatars);
  data.avatar = avatars?.avatar || avatars?.default_avatar;
  data.theme = data.theme ? formatTheme(data.theme) : undefined;
  return data as Space;
}

export function formatSpacePreview(data: AnyObject): SpacePreview {
  if (!data) return null;
  data.theme = formatTheme(data.theme);
  return data as unknown as SpacePreview;
}

// Called from within formatters on raw nested theme objects, so still
// responsible for its own camelCasing.
function formatTheme(data: unknown): SpaceTheme {
  if (!data) return null;

  const converted = data as AnyObject;
  if (converted.headerTitleStyle === "space_name") {
    converted.headerTitleStyle = ThemeHeaderTitleOptions.name;
  }
  return {
    headerBackgroundColor: "#FAFAFA",
    ...converted,
  } as SpaceTheme;
}

// MARK : Space Member
/*-------------------------------------------------------------------------------*/

export function formatSpaceMember(data: AnyObject): SpaceMember {
  if (!data) return data as SpaceMember;
  data.counts = data.kits;
  data.joinedKits =
    data.joinedKits ||
    asArr(data.kits).reduce<number>((res, k) => {
      const access = asObj((k as AnyObject).access);
      return res + (access?.status === "active" ? 1 : 0);
    }, 0);
  data.isPublic = Boolean(data.userId) === false || data.role === "guest";
  data.permissions = data.permissions || [];
  return data as unknown as SpaceMember;
}

// MARK : Portal
/*-------------------------------------------------------------------------------*/

export function formatPortal(data: AnyObject): Portal {
  if (!data) return null;
  data.urlId = slugifyName(data.name as string, data.shortId as string);
  return data as unknown as Portal;
}

// MARK : Portal Member
// -------------------------------------------------------------------------------

export function formatPortalMember(data: AnyObject): PortalMember {
  if (!data) return data as PortalMember;
  data.permissions = data.permissions || [];
  data.isPublic = Boolean(data.userId) === false;
  return data as unknown as PortalMember;
}

// MARK : Portal Item
/*-------------------------------------------------------------------------------*/

export function formatPortalItem(data: AnyObject): PortalItem {
  if (!data) return null;
  // kitId references the contained kit's id — read from the raw nested kit
  const kit = asObj(data.kit);
  data.kitId = kit?.kitId;
  const dat = asObj(data.data);
  if (dat) dat.backgroundColor = dat.backgroundColor || "default";
  return data as unknown as PortalItem;
}

// MARK : Kit
/*-------------------------------------------------------------------------------*/

export function formatKit(data: AnyObject): Kit {
  const access = asObj(data.access);
  const userVersion = access?.useVersion;
  const recommendedVersion = data.useVersion;
  data.urlId = slugifyName(data.name as string, data.shortId as string);
  data.shareToken = data.token;
  data.recommendedVersion = recommendedVersion;
  data.userVersion = userVersion == null ? recommendedVersion : userVersion;
  data.counts = data.counts || {};
  data.downloadsRestricted = data.downloadsRestricted || false;
  return data as unknown as Kit;
}

// MARK : Kit Member
/*-------------------------------------------------------------------------------*/

export function formatKitMember(data: AnyObject): KitMember | KitAccess {
  if (!data) return null;
  data.permissions = data.permissions || [];
  data.isFavorite = Boolean(data.isFavorite);
  data.isPublic = Boolean(data.userId) === false;
  return data as unknown as KitMember | KitAccess;
}

// MARK : Kit Version
/*-------------------------------------------------------------------------------*/

export function formatKitVersion(data: AnyObject): KitVersion {
  // Stamp kit_uuid onto each raw section so formatSection picks up kitId
  // when the walker recurses into it.
  const sections = asArr(data.sections, null);
  if (sections) {
    data.sections = sections.map(s => ({ ...asObj(s), kitId: data.kitId }));
  }
  return data as KitVersion;
}

// MARK : Section
/*-------------------------------------------------------------------------------*/

export function formatSection(data: AnyObject): Section {
  data.urlId = slugifyName(data.name as string, data.shortId as string);
  data.isSeparator = Boolean(
    (data.name as string)?.startsWith("---") && (data.counts as Section["counts"])?.items === 0
  );
  return data as unknown as Section;
}

// MARK : Item
/*-------------------------------------------------------------------------------*/

export function formatItem(data: AnyObject): Item {
  if (!data) return null;
  // `data.data` is still raw at this point (walker is shallow). Snapshot it
  // before we replace with the formatted itemData so display-property
  // defaults can keep reading raw snake_case keys.
  const rawData = asObj(data.data);
  const itemData = formatItemData(rawData);
  data.data = itemData;
  data.urlId = slugifyName(
    (itemData.viewName ?? itemData.name ?? (data.asset as AnyObject)?.name) as string,
    data.shortId as string
  );
  if (!data.asset && data.assetUuid) {
    data.asset = data.assetUuid;
  }
  data.displayProperties = formatDisplayPropertyDefaults(
    data.displayProperties as Item["displayProperties"],
    data.type as ItemType,
    rawData
  );
  return data as unknown as Item;
}

function formatItemData(data: AnyObject): Item["data"] {
  return {
    backgroundColor: "default",
    ...data,
  } as Item["data"];
}

function formatDisplayPropertyDefaults(
  dp: Item["displayProperties"] = {},
  type: ItemType,
  rawData: AnyObject | undefined
): Item["displayProperties"] {
  const defaults: Partial<Item["displayProperties"]> = {};

  switch (type) {
    case ItemType.asset: {
      const isFullWidth =
        ["leading", "trailing"].includes(dp.imageAlignment) ||
        rawData?.displayStyle === "full_width";
      defaults.allowDownload = true;
      defaults.showMetadata = true;
      defaults.size = isFullWidth ? 6 : 5;
      defaults.imageAlignment = isFullWidth ? "leading" : "top";
      defaults.cardBackgroundColor = !isFullWidth ? "clear" : "noteNeutralColor";
      break;
    }
    case ItemType.codeSnippet:
      defaults.size = 1;
      defaults.cardBackgroundColor = "noteNeutralColor";
      break;
    case ItemType.note:
    case ItemType.supportingImage:
      defaults.size = 1;
      defaults.cardBackgroundColor = rawData?.backgroundColor;
      defaults.showMetadata = false;
      defaults.allowDownload = false;
      break;
    case ItemType.gallery:
      defaults.size = 2;
      defaults.imageAlignment = "leading";
      defaults.cardBackgroundColor = rawData?.backgroundColor ?? "noteNeutralColor";
      break;
    case ItemType.guide:
      defaults.imageAlignment = "top";
      defaults.size = rawData?.displaySize > 0 ? 2 : 1;
      defaults.displayStyle = rawData?.displayStyle ?? "image";
      break;
  }

  const result = { ...defaults, ...dp };

  return result;
}
// MARK : Item.Asset
// asset is always contained in an item
/*-------------------------------------------------------------------------------*/

export function formatAsset(_data: unknown): Asset {
  const data = asObj(_data);
  if (!data) return null;
  if (!data.meta) data.meta = {};
  function decacheUrl(url: string) {
    if (typeof url !== "string") return url;
    return url.includes("?") ? `${url}&hash=${data.fileHash}` : `${url}?hash=${data.fileHash}`;
  }

  const res = data as AnyObject;
  // Support both post-camelize input (id already set from uuid) and direct
  // calls with raw snake_case data (uuid still present, id undefined).
  res.id = data.id ?? data.uuid;
  res.sourceId = data.sourceId;
  res.shareToken = data.token;
  Object.assign(res, getAssetDimensions(data));
  res.colors = ((data.colors as unknown[]) || []).map(formatAssetColor);
  res.isFont = assetIsFont(data);
  res.permalink = decacheUrl(data.permalink as string);
  if (data.thumbnails) {
    const thumbs = asObj(data.thumbnails);
    res.thumbnails = Object.keys(thumbs).reduce((acc: AnyObject, k) => {
      acc[k] = decacheUrl(thumbs[k] as string);
      return acc;
    }, {});
  }
  res.meta = formatAssetMeta(data.meta);
  return res as Asset;
}

function formatAssetColor(color: AnyObject): Color {
  const c = new TinyColor(
    `hsva(${color.hue}, ${color.saturation}%, ${color.brightness}%, ${color.alpha / 100})`
  );
  return {
    ...color,
    hex: c.toHexString(),
    hex8: c.toHex8String(),
    rgb: c.toRgbString(),
    hsb: c.toHsvString().replace("hsv", "hsb"),
    name: color.name,
    pms: color.pms || null,
    cmyk: color.cmyk || null,
  } as Color;
}

function formatAssetMeta(meta: AnyObject): Asset["meta"] {
  const { font, filecuts } = meta as { font?: AnyObject; filecuts?: AnyObject };
  return {
    ...meta,
    font: font && formatFontData(font as AnyObject),
    filecuts: {
      availableTypes: filecuts?.availableTypes || [],
      presets: filecuts?.presets || [],
    },
  } as Asset["meta"];
}

function formatFontData(data: AnyObject): Asset["meta"]["font"] {
  return {
    ...data,
    googleVariant: data.g_variant,
  } as Asset["meta"]["font"];
}

function assetIsFont(a: AnyObject) {
  if (a.type !== AssetType.textStyle) return false;
  const meta = asObj(a.meta);
  return Boolean(meta.textStyle && Object.keys(meta.textStyle).length);
}

function getAssetDimensions(a: AnyObject) {
  // Convert dimensions to ints (96.0x96.0 -> 96x96)
  const dimensions = a.dimensions as string;
  const comps = dimensions.split("x").map(px => Math.round(parseFloat(px)));

  return {
    dimensions: comps.join("x"),
    width: comps[0],
    height: comps[1],
  };
}

// MARK : Space Search
/*-------------------------------------------------------------------------------*/

// MARK : Products
/*-------------------------------------------------------------------------------*/

export function formatField(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  return {
    ...d,
    config: d.config ?? {},
  };
}

// MARK : Products
/*-------------------------------------------------------------------------------*/

export function formatProduct(data: AnyObject): Product {
  if (!data) return null;
  data.fields = formatPimFields(data.fields);
  data.variants = asArr(data.variants, null)?.map(formatVariant);
  return data as unknown as Product;
}

export function formatVariant(data: unknown) {
  if (!data) return data;
  const d = asObj(data);
  return {
    __formatted: true,
    ...d,
    fields: formatPimFields(d.fields),
  };
}

function formatPimFieldValue(raw: unknown): AnyObject {
  const v = asObj(raw);
  const out: AnyObject = {
    options: [],
    ...v,
  };
  if ("assets" in v) {
    out.assets = asArr(v.assets, []).map((entry: unknown) => {
      const a = asObj(entry);
      return {
        ...a,
        inherited: a.inherited ?? false,
      };
    });
  }
  return out;
}

export function formatPimFields(raw: unknown): Record<string, AnyObject> {
  if (!raw || typeof raw !== "object") return {};
  return Object.fromEntries(
    Object.entries(raw as AnyObject).map(([k, v]) => [k, formatPimFieldValue(v)])
  );
}

// MARK : Exports
/*-------------------------------------------------------------------------------*/

export const format = {
  item: formatItem,
  asset: formatAsset,
  section: formatSection,
  kit: formatKit,
  kitVersion: formatKitVersion,
  space: formatSpace,
  spacePreview: formatSpacePreview,
  spaceMember: formatSpaceMember,
  portal: formatPortal,
  portalMember: formatPortalMember,
  portalItem: formatPortalItem,
  kitMember: formatKitMember,
  field: formatField,
  product: formatProduct,
  variant: formatVariant,
};
